import {
  EndpointsEndpointResponse,
  PortainerEndpointSecuritySettings,
} from '@api/types.gen';

import {
  TeamAccessViewModel,
  UserAccessViewModel,
} from '@/portainer/models/access';
import { TagId } from '@/portainer/tags/types';
import { DockerSnapshot } from '@/react/docker/snapshots/types';

export type EnvironmentGroupId = number;

export type EdgeGroupId = number;

type RoleId = number;
interface AccessPolicy {
  RoleId: RoleId;
}

export type UserAccessPolicies = Record<number, AccessPolicy>; // map[UserID]AccessPolicy
export type TeamAccessPolicies = Record<number, AccessPolicy>;

export type EnvironmentId = number;

/**
 * matches portainer.EndpointType in app/portainer.go
 */
export enum EnvironmentType {
  // Docker represents an environment(endpoint) connected to a Docker environment(endpoint)
  Docker = 1,
  // AgentOnDocker represents an environment(endpoint) connected to a Portainer agent deployed on a Docker environment(endpoint)
  AgentOnDocker,
  // Azure represents an environment(endpoint) connected to an Azure environment(endpoint)
  Azure,
  // EdgeAgentOnDocker represents an environment(endpoint) connected to an Edge agent deployed on a Docker environment(endpoint)
  EdgeAgentOnDocker,
  // KubernetesLocal represents an environment(endpoint) connected to a local Kubernetes environment(endpoint)
  KubernetesLocal,
  // AgentOnKubernetes represents an environment(endpoint) connected to a Portainer agent deployed on a Kubernetes environment(endpoint)
  AgentOnKubernetes,
  // EdgeAgentOnKubernetes represents an environment(endpoint) connected to an Edge agent deployed on a Kubernetes environment(endpoint)
  EdgeAgentOnKubernetes,
}

export const EdgeTypes = [
  EnvironmentType.EdgeAgentOnDocker,
  EnvironmentType.EdgeAgentOnKubernetes,
] as const;

export enum EnvironmentStatus {
  Up = 1,
  Down,
  Provisioning,
  Error,
}

export interface KubernetesSnapshot {
  KubernetesVersion: string;
  TotalCPU: number;
  TotalMemory: number;
  Time: number;
  NodeCount: number;
}

export type IngressClass = {
  Name: string;
  Type: string;
  Blocked?: boolean;
  BlockedNamespaces?: string[] | null;
};

export interface StorageClass {
  Name: string;
  AccessModes: string[];
  AllowVolumeExpansion: boolean;
  Provisioner: string;
}

export interface KubernetesConfiguration {
  UseLoadBalancer?: boolean;
  StorageClasses?: StorageClass[];
  UseServerMetrics?: boolean;
  EnableResourceOverCommit?: boolean;
  ResourceOverCommitPercentage?: number;
  RestrictDefaultNamespace?: boolean;
  RestrictSecrets?: boolean;
  RestrictStandardUserIngressW?: boolean;
  IngressClasses: IngressClass[];
  IngressAvailabilityPerNamespace: boolean;
  AllowNoneIngressClass: boolean;
}

export interface KubernetesSettings {
  Snapshots?: KubernetesSnapshot[] | null;
  Configuration: KubernetesConfiguration;
  Flags: {
    IsServerMetricsDetected: boolean;
    IsServerIngressClassDetected: boolean;
    IsServerStorageDetected: boolean;
  };
}

export type EnvironmentEdge = {
  AsyncMode: boolean;
  PingInterval: number;
  SnapshotInterval: number;
  CommandInterval: number;
  TunnelServerAddr?: string;
};

export type EnvironmentSecuritySettings = PortainerEndpointSecuritySettings;

export type DeploymentOptions = {
  overrideGlobalOptions: boolean;
  hideAddWithForm: boolean;
  hideWebEditor: boolean;
  hideFileUpload: boolean;
};

type AddonWithArgs = {
  Name: string;
  Args?: string;
};

export enum K8sDistributionType {
  OMNI = 'omni',
}

export enum KaasProvider {
  CIVO = 'civo',
  LINODE = 'linode',
  DIGITAL_OCEAN = 'digitalocean',
  GOOGLE_CLOUD = 'gke',
  AWS = 'amazon',
  AZURE = 'azure',
}

export type CloudProviderSettings = {
  Name: 'Civo' | 'Linode' | 'Digital Ocean' | 'Google' | 'Azure' | 'Amazon';
  Provider: K8sDistributionType | KaasProvider;
  URL: string;
  Region: string | null;
  Size: number | null;
  NodeCount: number;
  CPU: number | null;
  AddonsWithArgs: AddonWithArgs[] | null;
  AmiType: number | null;
  CredentialID: number;
  DNSPrefix: string;
  HDD: number | null;
  InstanceType: string | null;
  KubernetesVersion: string;
  NetworkID: number | null;
  NodeIPs: string;
  OfflineInstall: boolean;
  NodeVolumeSize: number | null;
  PoolName: string;
  RAM: number | null;
  ResourceGroup: string;
  Tier: string;
};
/**
 *  EndpointChangeWindow determine when automatic stack/app updates may occur
 */
export interface EndpointChangeWindow {
  Enabled: boolean;
  StartTime: string;
  EndTime: string;
}

export type OperationStatus = 'processing' | 'error' | 'warning' | '';

export interface EnvironmentStatusMessage {
  detail: string;
  summary: string;
  operation: '' | 'scale' | 'upgrade' | 'addons';
  operationStatus: OperationStatus;
  warnings?: string[] | null;
}

type EnvironmentBase = Omit<
  EndpointsEndpointResponse,
  | 'Status'
  | 'Kubernetes'
  | 'Policies'
  | 'CloudProvider'
  | 'StatusMessage'
  | 'DeploymentOptions'
>;

export interface Environment extends EnvironmentBase {
  Status: EnvironmentStatus;
  Type: EnvironmentType;
  ContainerEngine: ContainerEngine;
  Kubernetes: KubernetesSettings;
  Policies?: EnvironmentPolicies;
  DeploymentOptions: DeploymentOptions | null;
  CloudProvider?: Partial<CloudProviderSettings> | null;
  StatusMessage?: EnvironmentStatusMessage;

  // Promote optional to required (fields that were required in original Environment)
  TagIds: TagId[];
  Snapshots: DockerSnapshot[];
}

export interface EnvironmentPolicies {
  changeConfirmationPolicy?: AppliedPolicy;
  dockerCleanupPolicy?: AppliedPolicy;
  dockerSecurityPolicy?: AppliedPolicy;
  dockerSetupPolicy?: AppliedPolicy;
  dockerRBACPolicy?: AppliedPolicy;
  dockerRegistryPolicy?: AppliedPolicy;
  k8sSecurityPolicy?: AppliedPolicy;
  k8sSetupPolicy?: AppliedPolicy;
  k8sRBACPolicy?: AppliedPolicy;
  k8sRegistryPolicy?: AppliedPolicy;
  k8sObservabilityPolicy?: AppliedPolicy;
}

export type AppliedPolicy = {
  policyID?: number;
  policyName?: string;
  policyStatus?: AppliedPolicyStatus;
};

export type AppliedPolicyStatus = {
  Message: string;
  Status: AppliedPolicyStatusType;
  /** Unix timestamp */
  LastAttemptTime?: number;
};

export enum AppliedPolicyStatusType {
  Applied = 'applied',
  Failed = 'failed',
  InProgress = 'in_progress',
  Warning = 'warning',
  NotSupported = 'not_supported',
}

/**
 * TS reference of endpoint_create.go#EndpointCreationType iota
 */
export enum EnvironmentCreationTypes {
  LocalDockerEnvironment = 1,
  AgentEnvironment,
  AzureEnvironment,
  EdgeAgentEnvironment,
  LocalKubernetesEnvironment,
  KubeConfigEnvironment,
}

export enum ContainerEngine {
  Docker = 'docker',
  Podman = 'podman',
  // an empty container engine means that the endpoint is a Kubernetes endpoint
  Kubernetes = '',
}

export enum PlatformType {
  Docker,
  Kubernetes,
  Azure,
  Podman,
}

export type Access = UserAccessViewModel | TeamAccessViewModel;

export interface Role {
  Id: number;
  Name: string;
  Priority: number;
}

export interface CreateAccessValues {
  selectedUsersAndTeams: Access[];
  selectedRoleId?: number;
}

export enum EnvironmentHealth {
  Down,
  Outdated,
  Up,
  Heartbeat,
}
